import {
    StripePartnerBusHealth,
    StripePartnerRuleHealth,
    StripeStateMachineHealth,
} from '../types';

// FNP-412: how the Verify Events panel reads each layer. Green means the layer
// saw traffic, yellow means nothing yet (metrics lag a few minutes) or a
// partner bus that receives events but routes none, grey means "expected" (the
// rule did not match events it is not meant to route), red means a failure.

export type LayerTone = 'green' | 'yellow' | 'gray' | 'red';

export type LayerBadge = {
    tone: LayerTone;
    label: string;
};

export const busBadge = (bus: StripePartnerBusHealth): LayerBadge => {
    if (bus.observability === 'NO_RULES') return {tone: 'yellow', label: bus.observabilityLabel};
    if (bus.eventsReceived > 0) return {tone: 'green', label: 'events received'};
    return {tone: 'yellow', label: 'no events yet'};
};

export const ruleBadge = (rule: StripePartnerRuleHealth, eventsReceived: number): LayerBadge => {
    if (rule.failedInvocations > 0 || (rule.deadLetterInvocations ?? 0) > 0) {
        return {tone: 'red', label: 'failed invocations'};
    }
    if (rule.matchedEvents > 0) return {tone: 'green', label: 'matched'};
    if (eventsReceived > 0) {
        return {tone: 'gray', label: 'no match: expected for events this rule does not route'};
    }
    return {tone: 'yellow', label: 'no events yet'};
};

export const stateMachineBadge = (sm: StripeStateMachineHealth): LayerBadge => {
    if (sm.executionsFailed + sm.executionsTimedOut + sm.executionsAborted > 0) {
        return {tone: 'red', label: 'executions did not succeed'};
    }
    if (sm.executionsStarted > 0) return {tone: 'green', label: 'executions started'};
    return {tone: 'gray', label: 'no executions'};
};

// arn:aws:<service>:<region>:<account>:<resource>
const regionOf = (arn: string) => arn.split(':')[3] ?? 'us-west-2';

const consoleBase = (service: string, region: string) =>
    `https://${region}.console.aws.amazon.com/${service}/home?region=${region}`;

export const busConsoleUrl = (bus: StripePartnerBusHealth) => {
    const region = regionOf(bus.eventBusArn);
    return `${consoleBase('events', region)}#/eventbus/${encodeURIComponent(bus.eventBusName)}`;
};

export const ruleConsoleUrl = (bus: StripePartnerBusHealth, rule: StripePartnerRuleHealth) => {
    const region = regionOf(rule.ruleArn);
    return `${consoleBase('events', region)}#/eventbus/${encodeURIComponent(bus.eventBusName)}/rules/${encodeURIComponent(rule.ruleName)}`;
};

export const stateMachineConsoleUrl = (sm: StripeStateMachineHealth) => {
    const region = regionOf(sm.stateMachineArn);
    return `${consoleBase('states', region)}#/statemachines/view/${encodeURIComponent(sm.stateMachineArn)}`;
};
